import { useEffect, useRef, useState } from "react";
import { blinkAt, breathAt, FRAGMENT, idleAt, lookAt, nextBlinkGap, VERTEX } from "./liveShader";

/** A live portrait's four pictures, as URLs this page may read pixels from. */
export type LiveSources = {
  image: string;
  /** Absent when there is no blink: eyes already shut, or no face found. */
  blink?: string;
  plate: string;
  depth: string;
};

/**
 * LivePortrait draws a character's portrait brought to life (requirement
 * 0059, ADR-0127): the head turns towards the pointer and wanders when left
 * alone, the chest breathes, the eyes blink. One WebGL shader over four
 * pictures; nothing is fetched or paid for while it runs.
 *
 * It shows the still `fallback` until the pictures have loaded, and keeps
 * showing it where WebGL is unavailable or the reader asked for less motion.
 */
export default function LivePortrait({
  sources,
  fallback,
  size,
  className = "",
}: {
  sources: LiveSources;
  /** The still portrait, shown first and wherever the live one cannot run. */
  fallback: string;
  size: number;
  className?: string;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const pointer = useRef<[number, number] | null>(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const gl = el.getContext("webgl", { premultipliedAlpha: false, antialias: false });
    if (!gl) return;

    let cancelled = false;
    let frame = 0;
    const textures: WebGLTexture[] = [];

    const compile = (type: number, source: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, source);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS))
        throw new Error(gl.getShaderInfoLog(s) ?? "shader");
      return s;
    };
    const load = (url: string) =>
      new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
      });

    (async () => {
      try {
        const [image, blink, plate, depth] = await Promise.all([
          load(sources.image),
          load(sources.blink ?? sources.image),
          load(sources.plate),
          load(sources.depth),
        ]);
        if (cancelled) return;
        const program = gl.createProgram()!;
        gl.attachShader(program, compile(gl.VERTEX_SHADER, VERTEX));
        gl.attachShader(program, compile(gl.FRAGMENT_SHADER, FRAGMENT));
        gl.linkProgram(program);
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error("link");
        gl.useProgram(program);

        const quad = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, quad);
        gl.bufferData(
          gl.ARRAY_BUFFER,
          new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
          gl.STATIC_DRAW,
        );
        const pos = gl.getAttribLocation(program, "aPos");
        gl.enableVertexAttribArray(pos);
        gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);

        [image, blink, plate, depth].forEach((img, unit) => {
          const tex = gl.createTexture()!;
          textures.push(tex);
          gl.activeTexture(gl.TEXTURE0 + unit);
          gl.bindTexture(gl.TEXTURE_2D, tex);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
          // Not a power of two: no mipmaps, and clamp at the edges.
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        });
        ["uImage", "uClosed", "uPlate", "uDepth"].forEach((name, unit) =>
          gl.uniform1i(gl.getUniformLocation(program, name), unit),
        );
        const uSize = gl.getUniformLocation(program, "uSize");
        const uLook = gl.getUniformLocation(program, "uLook");
        const uBreath = gl.getUniformLocation(program, "uBreath");
        const uBlink = gl.getUniformLocation(program, "uBlink");

        // Two faces on one page never wander in step.
        let t = Math.random() * 20;
        let look: [number, number] = [0, 0];
        let blinkStart = -10;
        let nextBlink = t + 1;
        let doubled = false;
        let last = performance.now();
        const blinks = !!sources.blink;

        const tick = (now: number) => {
          if (cancelled) return;
          const dt = Math.min((now - last) / 1000, 0.1);
          last = now;
          t += dt;
          const target = pointer.current ?? idleAt(t);
          const ease = 1 - Math.exp(-dt * (pointer.current ? 7 : 3));
          look = [look[0] + (target[0] - look[0]) * ease, look[1] + (target[1] - look[1]) * ease];
          if (blinks && t >= nextBlink) {
            blinkStart = t;
            const [gap, d] = nextBlinkGap(Math.random, doubled);
            nextBlink = t + gap;
            doubled = d;
          }
          const w = el.width;
          const h = el.height;
          gl.viewport(0, 0, w, h);
          gl.uniform2f(uSize, w, h);
          gl.uniform2f(uLook, look[0], look[1]);
          gl.uniform1f(uBreath, breathAt(t));
          gl.uniform1f(uBlink, blinks ? blinkAt(t - blinkStart) : 0);
          gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
          frame = requestAnimationFrame(tick);
        };
        setRunning(true);
        frame = requestAnimationFrame(tick);
      } catch {
        // The still stays up: a live portrait is never the only way to see a face.
      }
    })();

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      textures.forEach((tex) => gl.deleteTexture(tex));
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    };
  }, [sources.image, sources.blink, sources.plate, sources.depth]);

  const ratio = typeof window === "undefined" ? 1 : Math.min(window.devicePixelRatio || 1, 2);
  const follow = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    pointer.current = lookAt(e.clientX - box.left, e.clientY - box.top, box.width, box.height);
  };

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={{ width: size, height: size }}
      data-testid="live-portrait"
      data-running={running ? "true" : undefined}
    >
      <img
        src={fallback}
        alt=""
        width={size}
        height={size}
        className={`absolute inset-0 size-full object-cover transition-opacity duration-500 ${running ? "opacity-0" : ""}`}
      />
      <canvas
        ref={canvas}
        width={Math.round(size * ratio)}
        height={Math.round(size * ratio)}
        onPointerMove={follow}
        onPointerDown={follow}
        onPointerLeave={() => (pointer.current = null)}
        onPointerUp={(e) => e.pointerType !== "mouse" && (pointer.current = null)}
        className={`absolute inset-0 size-full touch-none transition-opacity duration-500 ${running ? "" : "opacity-0"}`}
      />
    </div>
  );
}
